import React, { useState, useEffect, useRef } from 'react'
import { useAuth } from '../contexts/AuthContext'
import { useNavigate } from 'react-router-dom'
import { HiUser, HiLocationMarker, HiCalendar, HiCheckCircle, HiXCircle, HiX } from 'react-icons/hi'
import toast from 'react-hot-toast'
import LocationMap from '../components/LocationMap'
import PlusCode from '../components/PlusCode'

const UserProfile = () => {
  const { user, token } = useAuth()
  const navigate = useNavigate()
  const [isEditing, setIsEditing] = useState(false)
  const [formData, setFormData] = useState({
    email: '',
    full_name: '',
    full_address: '',
    house_number: '',
    road: '',
    address2: '',
    city: '',
    state: '',
    postcode: '',
    country_iso2: '',
    mobile_phone: '',
    latitude: '',
    longitude: '',
    timezone: '',
    is_active: true,
    is_superuser: false,
    account_validated: false
  })
  const [originalFormData, setOriginalFormData] = useState(null)
  const [loading, setLoading] = useState(false)
  const [countries, setCountries] = useState([])
  const [countriesLoading, setCountriesLoading] = useState(false)
  const [originalLocation, setOriginalLocation] = useState(null)
  const [showDiscardDialog, setShowDiscardDialog] = useState(false)
  const hasUnsavedChanges = useRef(false)

  // Fetch countries from API
  const fetchCountries = async () => {
    setCountriesLoading(true)
    try {
      const response = await fetch('https://api.cityseecityfix.com/api/v1/iso2')
      if (response.ok) {
        const data = await response.json()
        setCountries(data)
      } else {
        console.error('Failed to fetch countries')
        toast.error('Failed to load countries list')
      }
    } catch (error) {
      console.error('Error fetching countries:', error)
      toast.error('Failed to load countries list')
    } finally {
      setCountriesLoading(false)
    }
  }

  // Phone number formatting function
  const formatPhoneNumber = (value) => {
    // Remove all non-numeric characters except +
    const phoneNumber = value.replace(/[^\d+]/g, '')
    
    // Handle international format with country code
    if (phoneNumber.startsWith('+1') && phoneNumber.length > 2) {
      const digits = phoneNumber.slice(2) // Remove +1
      if (digits.length >= 10) {
        return `+1 (${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6, 10)}`
      } else if (digits.length >= 6) {
        return `+1 (${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6)}`
      } else if (digits.length >= 3) {
        return `+1 (${digits.slice(0, 3)}) ${digits.slice(3)}`
      } else if (digits.length > 0) {
        return `+1 (${digits}`
      }
      return '+1'
    }
    
    // Handle 11-digit numbers starting with 1 (US country code without +)
    if (phoneNumber.length === 11 && phoneNumber.startsWith('1')) {
      const digits = phoneNumber.slice(1) // Remove leading 1
      return `+1 (${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6, 10)}`
    }
    
    // Handle US format without country code (10 digits)
    if (phoneNumber.length >= 10) {
      return `(${phoneNumber.slice(0, 3)}) ${phoneNumber.slice(3, 6)}-${phoneNumber.slice(6, 10)}`
    } else if (phoneNumber.length >= 6) {
      return `(${phoneNumber.slice(0, 3)}) ${phoneNumber.slice(3, 6)}-${phoneNumber.slice(6)}`
    } else if (phoneNumber.length >= 3) {
      return `(${phoneNumber.slice(0, 3)}) ${phoneNumber.slice(3)}`
    } else if (phoneNumber.length > 0) {
      return `(${phoneNumber}`
    }
    
    return phoneNumber
  }

  useEffect(() => {
    if (user) {
      const userData = {
        email: user.email || '',
        full_name: user.full_name || '',
        full_address: user.full_address || '',
        house_number: user.house_number || '',
        road: user.road || '',
        address2: user.address2 || '',
        city: user.city || '',
        state: user.state || '',
        postcode: user.postcode || '',
        country_iso2: user.country_iso2 || '',
        mobile_phone: formatPhoneNumber(user.mobile_phone || ''),
        latitude: user.latitude || '',
        longitude: user.longitude || '',
        timezone: user.timezone || '',
        is_active: user.is_active || false,
        is_superuser: user.is_superuser || false,
        account_validated: user.account_validated || false
      }
      
      // Build full address from individual fields
      userData.full_address = buildFullAddress(userData)
      
      setFormData(userData)
      setOriginalFormData(JSON.parse(JSON.stringify(userData))) // Deep copy for comparison
      
      // Store original location for distance calculation
      if (userData.latitude && userData.longitude) {
        setOriginalLocation({
          lat: parseFloat(userData.latitude),
          lng: parseFloat(userData.longitude)
        })
      }
    }
  }, [user])

  // Fetch countries on component mount
  useEffect(() => {
    fetchCountries()
  }, [])

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target
    
    // Format phone number if it's the mobile_phone field
    if (name === 'mobile_phone') {
      const formattedValue = formatPhoneNumber(value)
      setFormData(prev => {
        const newData = { ...prev, [name]: formattedValue }
        checkForChanges(newData)
        return newData
      })
    } else {
      setFormData(prev => {
        const newData = {
          ...prev,
          [name]: type === 'checkbox' ? checked : value
        }
        
        // Auto-update full_address when address fields change
        if (['house_number', 'road', 'address2', 'city', 'state', 'postcode', 'country_iso2'].includes(name)) {
          newData.full_address = buildFullAddress(newData)
        }
        
        checkForChanges(newData)
        return newData
      })
    }
  }

  // Check for changes and update the hasUnsavedChanges flag
  const checkForChanges = (currentData) => {
    if (!originalFormData) return
    
    const hasChanges = JSON.stringify(currentData) !== JSON.stringify(originalFormData)
    hasUnsavedChanges.current = hasChanges
  }

  // Build full address from individual address fields
  const buildFullAddress = (data) => {
    const parts = []
    
    // Add house number and road
    if (data.house_number && data.road) {
      parts.push(`${data.house_number} ${data.road}`)
    } else if (data.house_number) {
      parts.push(data.house_number)
    } else if (data.road) {
      parts.push(data.road)
    }
    
    // Add address2 if present
    if (data.address2) {
      parts.push(data.address2)
    }
    
    // Add city, state, postcode
    const cityStateZip = []
    if (data.city) cityStateZip.push(data.city)
    if (data.state) cityStateZip.push(data.state)
    if (data.postcode) cityStateZip.push(data.postcode)
    
    if (cityStateZip.length > 0) {
      parts.push(cityStateZip.join(', '))
    }
    
    // Add country code
    if (data.country_iso2) {
      parts.push(data.country_iso2)
    }
    
    return parts.join(', ')
  }


  const handleSave = async () => {
    setLoading(true)
    try {
      // Prepare data for API - preserve +1 prefix if present, otherwise just digits
      let phoneForAPI = formData.mobile_phone
      if (phoneForAPI.startsWith('+1')) {
        // Keep +1 prefix and add remaining digits
        phoneForAPI = '+1' + phoneForAPI.replace(/[^\d]/g, '')
      } else {
        // Check if it's an 11-digit number starting with 1
        const digitsOnly = phoneForAPI.replace(/\D/g, '')
        if (digitsOnly.length === 11 && digitsOnly.startsWith('1')) {
          // Add +1 prefix for 11-digit numbers starting with 1
          phoneForAPI = '+1' + digitsOnly
        } else {
          // Just digits for US numbers without country code
          phoneForAPI = digitsOnly
        }
      }
      
      const apiData = {
        ...formData,
        mobile_phone: phoneForAPI
      }
      
      const response = await fetch(`https://api.cityseecityfix.com/api/v1/users/${user.id}`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(apiData)
      })

      if (response.ok) {
        toast.success('Profile updated successfully!')
        setIsEditing(false)
        hasUnsavedChanges.current = false
        // Update original form data to reflect saved state
        setOriginalFormData(JSON.parse(JSON.stringify(formData)))
        // Refresh user data
        window.location.reload()
      } else {
        const errorData = await response.json()
        toast.error(errorData.detail || 'Failed to update profile')
      }
    } catch (error) {
      console.error('Error updating profile:', error)
      toast.error('Failed to update profile')
    } finally {
      setLoading(false)
    }
  }

  const handleCancel = () => {
    if (originalFormData) {
      setFormData(originalFormData)
    }
    setIsEditing(false)
    hasUnsavedChanges.current = false
  }

  const handleClose = () => {
    if (hasUnsavedChanges.current) {
      setShowDiscardDialog(true)
    } else {
      navigate('/dashboard')
    }
  }

  const handleDiscardChanges = () => {
    setShowDiscardDialog(false)
    navigate('/dashboard')
  }

  const handleKeepEditing = () => {
    setShowDiscardDialog(false)
  }

  // Handle location change from map
  const handleLocationChange = (lat, lng) => {
    setFormData(prev => {
      const newData = {
        ...prev,
        latitude: lat.toString(),
        longitude: lng.toString()
      }
      checkForChanges(newData)
      return newData
    })
  }

  // Handle address update from reverse geocoding
  const handleAddressUpdate = (addressData) => {
    setFormData(prev => {
      const newData = {
        ...prev,
        house_number: addressData.house_number,
        road: addressData.road,
        address2: addressData.address2 || '',
        city: addressData.city,
        state: addressData.state,
        postcode: addressData.postcode,
        country_iso2: addressData.country_iso2,
        full_address: addressData.full_address
      }
      checkForChanges(newData)
      return newData
    })
  }

  if (!user) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600 mx-auto"></div>
          <p className="mt-4 text-gray-600 dark:text-gray-400">Loading profile...</p>
        </div>
      </div>
    )
  }

  return (
    <div className="max-w-4xl mx-auto p-6">
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-lg">
        {/* Header */}
        <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700">
          <div className="flex items-center justify-between">
            {/* Left side - Close button */}
            <div className="flex items-center space-x-3">
              <button
                onClick={handleClose}
                className="p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
                title="Close and return to dashboard"
              >
                <HiX className="h-6 w-6 text-gray-600 dark:text-gray-400" />
              </button>
              <div className="flex items-center space-x-3">
                <div className="p-2 bg-primary-100 dark:bg-primary-900 rounded-lg">
                  <HiUser className="h-6 w-6 text-primary-600 dark:text-primary-400" />
                </div>
                <div>
                  <h1 className="text-2xl font-bold text-gray-900 dark:text-white">User Profile</h1>
                  <p className="text-gray-600 dark:text-gray-400">Manage your account information</p>
                </div>
              </div>
            </div>
            
            {/* Right side - Action buttons */}
            <div className="flex space-x-3">
              {!isEditing ? (
                <button
                  onClick={() => setIsEditing(true)}
                  className="px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors"
                >
                  Edit Profile
                </button>
              ) : (
                <>
                  <button
                    onClick={handleCancel}
                    className="px-4 py-2 bg-gray-500 text-white rounded-lg hover:bg-gray-600 transition-colors"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={handleSave}
                    disabled={loading}
                    className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors disabled:opacity-50"
                  >
                    {loading ? 'Saving...' : 'Save Changes'}
                  </button>
                </>
              )}
            </div>
          </div>
        </div>

        {/* Profile Content */}
        <div className="p-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* Personal Information */}
            <div className="space-y-4">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white flex items-center">
                <HiUser className="h-5 w-5 mr-2 text-primary-600" />
                Personal Information
              </h3>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Email Address
                </label>
                <input
                  type="email"
                  name="email"
                  value={formData.email}
                  onChange={handleInputChange}
                  disabled={!isEditing}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white disabled:bg-gray-100 dark:disabled:bg-gray-800 disabled:text-gray-500"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Full Name
                </label>
                <input
                  type="text"
                  name="full_name"
                  value={formData.full_name}
                  onChange={handleInputChange}
                  disabled={!isEditing}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white disabled:bg-gray-100 dark:disabled:bg-gray-800 disabled:text-gray-500"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Mobile Phone
                </label>
                <input
                  type="tel"
                  name="mobile_phone"
                  value={formData.mobile_phone}
                  onChange={handleInputChange}
                  disabled={!isEditing}
                  placeholder="+1 (555) 123-4567 or (555) 123-4567"
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white disabled:bg-gray-100 dark:disabled:bg-gray-800 disabled:text-gray-500"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Timezone
                </label>
                <select
                  name="timezone"
                  value={formData.timezone}
                  onChange={handleInputChange}
                  disabled={!isEditing}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white disabled:bg-gray-100 dark:disabled:bg-gray-800 disabled:text-gray-500"
                >
                  <option value="">Select timezone...</option>
                  <option value="America/New_York">Eastern Time (ET)</option>
                  <option value="America/Chicago">Central Time (CT)</option>
                  <option value="America/Denver">Mountain Time (MT)</option>
                  <option value="America/Los_Angeles">Pacific Time (PT)</option>
                  <option value="America/Anchorage">Alaska Time (AKT)</option>
                  <option value="Pacific/Honolulu">Hawaii Time (HST)</option>
                  <option value="UTC">UTC (Coordinated Universal Time)</option>
                  <option value="Europe/London">London (GMT/BST)</option>
                  <option value="Europe/Paris">Paris (CET/CEST)</option>
                  <option value="Europe/Berlin">Berlin (CET/CEST)</option>
                  <option value="Asia/Tokyo">Tokyo (JST)</option>
                  <option value="Asia/Shanghai">Shanghai (CST)</option>
                  <option value="Asia/Kolkata">Mumbai (IST)</option>
                  <option value="Australia/Sydney">Sydney (AEST/AEDT)</option>
                </select>
              </div>
            </div>

            {/* Address Information */}
            <div className="space-y-4">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white flex items-center">
                <HiLocationMarker className="h-5 w-5 mr-2 text-primary-600" />
                Address Information
              </h3>
              
              <div>
                <div className="flex items-center justify-between mb-1">
                  <label className="text-sm font-medium text-gray-700 dark:text-gray-300">
                    Full Address <span className="text-xs text-gray-500">(Auto-generated)</span>
                  </label>
                  <PlusCode plusCode={formData.plus_code} />
                </div>
                <input
                  type="text"
                  name="full_address"
                  value={formData.full_address}
                  disabled
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-gray-100 dark:bg-gray-800 text-gray-500 dark:text-gray-400 cursor-not-allowed"
                />
                <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                  This field is automatically updated when you change the address fields below.
                </p>
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    House Number
                  </label>
                  <input
                    type="text"
                    name="house_number"
                    value={formData.house_number}
                    onChange={handleInputChange}
                    disabled={!isEditing}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white disabled:bg-gray-100 dark:disabled:bg-gray-800 disabled:text-gray-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Road
                  </label>
                  <input
                    type="text"
                    name="road"
                    value={formData.road}
                    onChange={handleInputChange}
                    disabled={!isEditing}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white disabled:bg-gray-100 dark:disabled:bg-gray-800 disabled:text-gray-500"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Address Line 2
                </label>
                <input
                  type="text"
                  name="address2"
                  value={formData.address2}
                  onChange={handleInputChange}
                  disabled={!isEditing}
                  placeholder="Apartment, suite, unit, etc."
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white disabled:bg-gray-100 dark:disabled:bg-gray-800 disabled:text-gray-500"
                />
              </div>

              <div className="grid grid-cols-3 gap-3">
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    City
                  </label>
                  <input
                    type="text"
                    name="city"
                    value={formData.city}
                    onChange={handleInputChange}
                    disabled={!isEditing}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white disabled:bg-gray-100 dark:disabled:bg-gray-800 disabled:text-gray-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    State
                  </label>
                  <input
                    type="text"
                    name="state"
                    value={formData.state}
                    onChange={handleInputChange}
                    disabled={!isEditing}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white disabled:bg-gray-100 dark:disabled:bg-gray-800 disabled:text-gray-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Postcode
                  </label>
                  <input
                    type="text"
                    name="postcode"
                    value={formData.postcode}
                    onChange={handleInputChange}
                    disabled={!isEditing}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white disabled:bg-gray-100 dark:disabled:bg-gray-800 disabled:text-gray-500"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Country
                </label>
                <select
                  name="country_iso2"
                  value={formData.country_iso2}
                  onChange={handleInputChange}
                  disabled={!isEditing || countriesLoading}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white disabled:bg-gray-100 dark:disabled:bg-gray-800 disabled:text-gray-500"
                >
                  <option value="">Select a country...</option>
                  {countries.map((country) => (
                    <option key={country.iso2} value={country.iso2}>
                      {country.name} ({country.iso2})
                    </option>
                  ))}
                </select>
                {countriesLoading && (
                  <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                    Loading countries...
                  </p>
                )}
              </div>
            </div>
          </div>

          {/* Location Coordinates */}
          <div className="mt-6 pt-6 border-t border-gray-200 dark:border-gray-700">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white flex items-center mb-4">
              <HiLocationMarker className="h-5 w-5 mr-2 text-primary-600" />
              Location Coordinates
            </h3>
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {/* Coordinates Input */}
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Latitude
                  </label>
                  <input
                    type="number"
                    step="any"
                    name="latitude"
                    value={formData.latitude}
                    onChange={handleInputChange}
                    disabled={!isEditing}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white disabled:bg-gray-100 dark:disabled:bg-gray-800 disabled:text-gray-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Longitude
                  </label>
                  <input
                    type="number"
                    step="any"
                    name="longitude"
                    value={formData.longitude}
                    onChange={handleInputChange}
                    disabled={!isEditing}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white disabled:bg-gray-100 dark:disabled:bg-gray-800 disabled:text-gray-500"
                  />
                </div>
              </div>

              {/* Map */}
              <LocationMap
                latitude={formData.latitude}
                longitude={formData.longitude}
                onLocationChange={handleLocationChange}
                onAddressUpdate={handleAddressUpdate}
                editable={isEditing}
                height="h-64 lg:h-80"
                originalLocation={originalLocation}
              />
            </div>
          </div>

          {/* Account Status */}
          <div className="mt-6 pt-6 border-t border-gray-200 dark:border-gray-700">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white flex items-center mb-4">
              <HiCheckCircle className="h-5 w-5 mr-2 text-primary-600" />
              Account Status
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div className="flex items-center space-x-3">
                <input
                  type="checkbox"
                  name="is_active"
                  checked={formData.is_active}
                  onChange={handleInputChange}
                  disabled={!isEditing}
                  className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded disabled:opacity-50"
                />
                <label className="text-sm font-medium text-gray-700 dark:text-gray-300">
                  Account Active
                </label>
              </div>
              <div className="flex items-center space-x-3">
                <input
                  type="checkbox"
                  name="is_superuser"
                  checked={formData.is_superuser}
                  onChange={handleInputChange}
                  disabled={!isEditing}
                  className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded disabled:opacity-50"
                />
                <label className="text-sm font-medium text-gray-700 dark:text-gray-300">
                  Super User
                </label>
              </div>
              <div className="flex items-center space-x-3">
                <input
                  type="checkbox"
                  name="account_validated"
                  checked={formData.account_validated}
                  onChange={handleInputChange}
                  disabled={!isEditing}
                  className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded disabled:opacity-50"
                />
                <label className="text-sm font-medium text-gray-700 dark:text-gray-300">
                  Account Validated
                </label>
              </div>
            </div>
          </div>

          {/* Read-only Fields */}
          <div className="mt-6 pt-6 border-t border-gray-200 dark:border-gray-700">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white flex items-center mb-4">
              <HiCalendar className="h-5 w-5 mr-2 text-primary-600" />
              System Information
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  User ID
                </label>
                <input
                  type="text"
                  value={user.id}
                  disabled
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-gray-100 dark:bg-gray-800 text-gray-500 dark:text-gray-400"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Created At
                </label>
                <input
                  type="text"
                  value={user.created_at ? new Date(user.created_at).toLocaleString() : 'N/A'}
                  disabled
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-gray-100 dark:bg-gray-800 text-gray-500 dark:text-gray-400"
                />
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Discard Changes Dialog */}
      {showDiscardDialog && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-20 mx-auto p-5 border w-96 shadow-lg rounded-md bg-white dark:bg-gray-800">
            <div className="mt-3 text-center">
              <div className="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-yellow-100 dark:bg-yellow-900">
                <HiXCircle className="h-6 w-6 text-yellow-600 dark:text-yellow-400" />
              </div>
              <h3 className="text-lg font-medium text-gray-900 dark:text-white mt-4">
                Unsaved Changes
              </h3>
              <div className="mt-2 px-7 py-3">
                <p className="text-sm text-gray-500 dark:text-gray-400">
                  You have unsaved changes. Are you sure you want to discard them and return to the dashboard?
                </p>
              </div>
              <div className="flex justify-center space-x-3 mt-4">
                <button
                  onClick={handleKeepEditing}
                  className="px-4 py-2 bg-gray-300 hover:bg-gray-400 text-gray-700 font-medium rounded-lg transition-colors"
                >
                  Keep Editing
                </button>
                <button
                  onClick={handleDiscardChanges}
                  className="px-4 py-2 bg-red-600 hover:bg-red-700 text-white font-medium rounded-lg transition-colors"
                >
                  Discard Changes
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

export default UserProfile
